(ns fen.mui
  "The components the window is drawn with, and the one theme they are given.

  Material is a look as much as it is a library, and ento's look is not
  Material's: the colours are `fen.theme`'s, the type is monospace, and the
  rows sit as close together as they did when each one carried its own
  style. What MUI is here for is the behaviour underneath - a dialog that
  takes the focus and hands it back, a list item that says it was touched, a
  field that knows it is a field - and that behaviour is worth more than the
  hundred lines of inline style it replaces.

  One component at a time, never the whole package: a release is compiled
  with `:simple`, so what is required here is what is shipped to the phone.

  This file knows no more about entities than the rest of `src/fen` does. It
  is a vocabulary, not a screen."
  ;; `Box` and `List` are names cljs.core already has. Here they are what
  ;; MUI calls them, because a component is looked up by the name the
  ;; documentation gives it and no other.
  (:refer-clojure :exclude [Box List])
  (:require-macros [kc.core :refer [def-ui $]])
  (:require ["@mui/material/styles" :as mui-styles]
            ["@mui/material/CssBaseline$default" :as mui-css-baseline]
            ["@mui/material/Alert$default" :as mui-alert]
            ["@mui/material/Box$default" :as mui-box]
            ["@mui/material/Button$default" :as mui-button]
            ["@mui/material/Chip$default" :as mui-chip]
            ["@mui/material/Dialog$default" :as mui-dialog]
            ["@mui/material/DialogActions$default" :as mui-dialog-actions]
            ["@mui/material/DialogContent$default" :as mui-dialog-content]
            ["@mui/material/DialogTitle$default" :as mui-dialog-title]
            ["@mui/material/List$default" :as mui-list]
            ["@mui/material/ListItemButton$default" :as mui-list-item-button]
            ["@mui/material/ListItemText$default" :as mui-list-item-text]
            ["@mui/material/ListSubheader$default" :as mui-list-subheader]
            ["@mui/material/TextField$default" :as mui-text-field]
            ["@mui/material/Typography$default" :as mui-typography]
            [fen.theme :refer [colors font]]))

;;; The vocabulary. Named once here so a screen says `mui/Button` and never
;;; a module path.

(def Alert mui-alert)
(def Box mui-box)
(def Button mui-button)
(def Chip mui-chip)
(def Dialog mui-dialog)
(def DialogActions mui-dialog-actions)
(def DialogContent mui-dialog-content)
(def DialogTitle mui-dialog-title)
(def List mui-list)
(def ListItemButton mui-list-item-button)
(def ListItemText mui-list-item-text)
(def ListSubheader mui-list-subheader)
(def TextField mui-text-field)
(def Typography mui-typography)

(defn sx
  "A style for MUI, as a map. MUI reads a plain object and nothing else, and
  a map that went unconverted is a style that silently does nothing - so
  every `:sx` in this window goes through here and it is visible when one
  did not."
  [m]
  (clj->js m))

(def ^:private line (str "1px solid " (:line colors)))

(def theme
  "ento's colours, told to MUI.

  The overrides are all of one kind: Material is built for a form to be
  filled in with a thumb, and this is an outline to be read - so the
  paddings come down, the type stays the one the window was written in, and
  a button is a word again rather than a shouted one. What is left of
  Material is what nobody should have to write twice: the focus ring, the
  ripple, the way a dialog behaves.

  What a finger needs is not decided here. That is a question about the
  pointer, and the pointer is something CSS knows (see public/index.html)."
  (mui-styles/createTheme
   (clj->js
    {:palette
     {:mode       "dark"
      :background {:default (:bg colors) :paper (:panel colors)}
      :text       {:primary (:fg colors) :secondary (:dim colors)}
      :primary    {:main (:key colors)}
      :secondary  {:main (:ref colors)}
      :error      {:main (:warn colors)}
      :warning    {:main (:seed colors)}
      :divider    (:line colors)}

     :shape {:borderRadius 4}

     :typography
     {:fontFamily font
      :fontSize   14
      :button     {:textTransform "none" :fontWeight 400}
      :caption    {:fontSize ".85em"}}

     :components
     {;; Dark Material lightens a raised surface to say it is raised. Here
      ;; a dialog is told apart by its border and by a deeper black behind
      ;; it, and the surface itself is one shade off the page.
      :MuiPaper {:styleOverrides {:root {:backgroundImage "none"}}}

      :MuiButton {:defaultProps  {:size "small" :color "inherit"}
                  :styleOverrides {:root {:minWidth 0 :fontSize "inherit"
                                          :lineHeight "inherit"
                                          :fontFamily "inherit"}}}

      ;; Where an entity stands, in one word. Small enough to sit on the
      ;; same line as the title it belongs to; the colour is the origin's
      ;; and is given where the chip is drawn (see fen.screen/Origin).
      :MuiChip {:defaultProps   {:size "small" :variant "outlined"}
                :styleOverrides {:root  {:height "auto" :fontSize ".75em"
                                         :borderRadius 3
                                         :color (:dim colors)
                                         :borderColor (:line colors)}
                                 :label {:padding "0 .4rem" :lineHeight 1.6}}}

      :MuiListItemButton
      {:styleOverrides
       {:root {:alignItems "stretch"
               :transition "background-color .08s"
               "&:hover"              {:backgroundColor (:hover colors)}
               ;; The cursor, as a colour and a bar. Inset, so the row does
               ;; not move sideways by two pixels as the cursor arrives.
               "&.Mui-selected"       {:backgroundColor (:cursor colors)
                                       :boxShadow (str "inset 2px 0 0 " (:key colors))}
               "&.Mui-selected:hover" {:backgroundColor (:cursor colors)}}}}

      :MuiListItemText {:styleOverrides {:root {:margin 0}}}

      ;; Whose offers these are. Material's subheader is a line of its own
      ;; height with a background to stick against; here it is a dim word
      ;; over the group it names.
      :MuiListSubheader
      {:styleOverrides {:root {:background "transparent"
                               :color (:dim colors)
                               :fontSize ".75em"
                               :letterSpacing ".08em"
                               :textTransform "uppercase"
                               :lineHeight "inherit"
                               :padding ".5rem .75rem .15rem"
                               :whiteSpace "nowrap"
                               :overflow "hidden"
                               :textOverflow "ellipsis"}}}

      :MuiDialog {:defaultProps   {:fullWidth true}
                  :styleOverrides {:paper {:border line :margin "0 .75rem"
                                           :boxShadow "0 24px 80px rgba(0,0,0,.7)"}}}

      :MuiDialogTitle {:styleOverrides {:root {:display "flex"
                                               :alignItems "baseline"
                                               :gap ".5rem"
                                               :padding ".5rem .75rem"
                                               :fontSize "1em"
                                               :fontWeight 700
                                               :borderBottom line}}}

      :MuiDialogContent {:styleOverrides {:root {:padding ".75rem"}}}

      :MuiDialogActions {:styleOverrides {:root {:padding ".5rem .75rem"
                                                 :gap ".5rem"
                                                 :borderTop line}}}

      :MuiInputBase {:styleOverrides {:root {:font "inherit"}}}
      :MuiOutlinedInput
      {:styleOverrides
       {:root {"& .MuiOutlinedInput-notchedOutline" {:borderColor (:line colors)}
               "&:hover .MuiOutlinedInput-notchedOutline" {:borderColor (:dim colors)}}}}}})))

(def-ui Frame
  "Everything the window draws, inside the one theme.

  `CssBaseline` as well, because it is what makes the page the colour of the
  theme rather than the browser's white - and the page is what is behind a
  phone's bounce at the end of a scroll."
  [***]
  ($ mui-styles/ThemeProvider {:theme theme}
     ($ mui-css-baseline)
     ***))
